import React, { useState, useEffect } from "react";
import { styled, useTheme } from '@mui/material/styles';
import Paper from "@mui/material/Paper";
import Downloads from "./downloads/Downloads";
import Button from "@mui/material/Button";
import Moment from "react-moment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCity,
  faDollarSign,
  faDatabase,
  faEdit,
  faMap,
  faPlusCircle,
  faMinusCircle,
} from "@fortawesome/free-solid-svg-icons";
import earthquakeIcon from "../../../assets/images/earthquake.png";
import floodIcon from "../../../assets/images/flood.png";
import hurricaneIcon from "../../../assets/images/hurricane.png";
import tsunamiIcon from "../../../assets/images/tsunami.png";

import { connect } from "react-redux";
import PropTypes from "prop-types";
import { setAnalysisGeom } from "../../actions/details";

const PREFIX = 'Analysis';

const classes = {
  root: `${PREFIX}-root`,
  styledPaper: `${PREFIX}-styledPaper`,
  analysis: `${PREFIX}-analysis`,
  summary: `${PREFIX}-summary`,
  description: `${PREFIX}-description`,
  header: `${PREFIX}-header`,
  subheader: `${PREFIX}-subheader`,
  meta: `${PREFIX}-meta`,
  metaName: `${PREFIX}-metaName`,
  downloads: `${PREFIX}-downloads`,
  controlButtonContainer: `${PREFIX}-controlButtonContainer`,
  controlButton: `${PREFIX}-controlButton`,
  controlText: `${PREFIX}-controlText`,
  mapIconToggle: `${PREFIX}-mapIconToggle`,
  metaItemContainer: `${PREFIX}-metaItemContainer`,
  metaItem: `${PREFIX}-metaItem`,
  metaIconMultiContainer: `${PREFIX}-metaIconMultiContainer`,
  metaIconContainer: `${PREFIX}-metaIconContainer`,
  metaIconContainerSpaced: `${PREFIX}-metaIconContainerSpaced`,
  metaIcon: `${PREFIX}-metaIcon`,
  metaIconTitle: `${PREFIX}-metaIconTitle`,
  metaIconLabel: `${PREFIX}-metaIconLabel`,
  hazards: `${PREFIX}-hazards`,
  hazard: `${PREFIX}-hazard`,
  hazardImage: `${PREFIX}-hazardImage`,
  hazardTitle: `${PREFIX}-hazardTitle`,
  mapIcon: `${PREFIX}-mapIcon`,
  metaInfoCap: `${PREFIX}-metaInfoCap`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  
  [`& .${classes.styledPaper}`]: {
	flexWrap: "wrap",
    height: "auto",
    width: "100%",
    margin: "1em 0",
    position: "relative",
    display: "flex",
    padding: "1em",
    "& > *": {
      margin: theme.spacing(1),
      width: theme.spacing(16),
      height: theme.spacing(16),
    },
    [theme.breakpoints.down("sm")]: {
      width: "auto",
    },
  },

  [`& .${classes.analysis}`]: {
    display: "flex",
    width: "100%",
    height: "auto",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.summary}`]: {
    width: "15em",
    height: "auto",
    flex: 1,
  },

  [`& .${classes.description}`]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      margin: "0 0 1em 0",
    },
  },

  [`& .${classes.header}`]: {
    fontWeight: "bold",
    fontSize: "1.2rem",
    display: "block",
    marginBottom: "1em",
  },

  [`& .${classes.subheader}`]: {
    fontSize: "1.2rem",
  },

  [`& .${classes.meta}`]: {
    display: "flex",
    fontSize: "0.8rem",
    margin: "0.3em 0",
    "& p": {
      margin: 0,
    },
  },

  [`& .${classes.metaName}`]: {
    fontWeight: "bold",
    marginRight: "0.3em",
  },

  [`& .${classes.downloads}`]: {
    display: "grid",
  },

  [`& .${classes.controlButtonContainer}`]: {
    display: "flex",
    justifyContent: "flex-end",
    "&:first-child": {
      borderRight: "1px solid #909090",
    },
  },

  [`& .${classes.controlButton}`]: {
    color: "#4c4c4c",
    marginBottom: "-1em",
    padding: 0,
    "& > *": {
      margin: theme.spacing(1),
    },
  },

  [`& .${classes.controlText}`]: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.mapIconToggle}`]: {
    position: "absolute",
    top: "6px",
    right: "6px",
    fontSize: "12px",
    [theme.breakpoints.down("sm")]: {
      right: "16px",
    },
  },

  [`& .${classes.metaItemContainer}`]: {
    display: "flex",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.metaItem}`]: {
    display: "flex",
    flexDirection: "column",
    margin: "0 1em",
    fontSize: "0.8rem",
    [theme.breakpoints.down("sm")]: {
      margin: "1em 0",
    },
  },

  [`& .${classes.metaIconMultiContainer}`]: {
    display: "flex",
  },

  [`& .${classes.metaIconContainer}`]: {
    display: "flex",
    alignItems: "center",
  },

  [`& .${classes.metaIconContainerSpaced}`]: {
    display: "flex",
    alignItems: "center",
    marginLeft: "2em",
    "&:first-child": {
      marginLeft: 0,
    },
  },

  [`& .${classes.metaIcon}`]: {
    fontSize: "1.8rem",
    height: "1em",
    marginRight: "0.2em",
    color: "#005287",
  },

  [`& .${classes.metaIconTitle}`]: {
    color: "#4c4c4c",
    width: "100%",
    borderBottom: "1px solid #d3d3d3",
    paddingBottom: "2px",
    fontSize: "0.7rem",
    textAlign: "left",
    textTransform: "uppercase",
    marginBottom: "0.5em",
  },

  [`& .${classes.metaIconLabel}`]: {
    textTransform: "capitalize",
  },

  [`& .${classes.hazards}`]: {
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.hazard}`]: {
    display: "flex",
    alignItems: "center",
    height: "4em",
  },

  [`& .${classes.hazardImage}`]: {
    height: "1.6em",
    margin: "auto",
  },

  [`& .${classes.hazardTitle}`]: {
    fontSize: "0.8rem",
    color: "#757575",
    textTransform: "capitalize",
  },

  [`& .${classes.mapIcon}`]: {
    margin: "0 0.4rem",
  },

  [`& .${classes.metaInfoCap}`]: {
    textTransform: "capitalize",
  }
}));

const StyledPaper = styled(Paper)((
  {
    theme
  }
) => ({
  
[`& .${classes.styledPaper}`]: {
	flexWrap: "wrap",
    height: "auto",
    width: "100%",
    margin: "1em 0",
    position: "relative",
    display: "flex",
    padding: "1em",
    "& > *": {
      margin: theme.spacing(1),
      width: theme.spacing(16),
      height: theme.spacing(16),
    },
    [theme.breakpoints.down("sm")]: {
      width: "auto",
    },
  },

  [`& .${classes.analysis}`]: {
    display: "flex",
    width: "100%",
    height: "auto",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.summary}`]: {
    width: "15em",
    height: "auto",
    flex: 1,
  },

  [`& .${classes.description}`]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      margin: "0 0 1em 0",
    },
  },

  [`& .${classes.header}`]: {
    fontWeight: "bold",
    fontSize: "1.2rem",
    display: "block",
    marginBottom: "1em",
  },

  [`& .${classes.subheader}`]: {
    fontSize: "1.2rem",
  },

  [`& .${classes.meta}`]: {
    display: "flex",
    fontSize: "0.8rem",
    margin: "0.3em 0",
    "& p": {
      margin: 0,
    },
  },

  [`& .${classes.metaName}`]: {
    fontWeight: "bold",
    marginRight: "0.3em",
  },

  [`& .${classes.downloads}`]: {
    display: "grid",
  },

  [`& .${classes.controlButtonContainer}`]: {
    display: "flex",
    justifyContent: "flex-end",
    "&:first-child": {
      borderRight: "1px solid #909090",
    },
  },

  [`& .${classes.controlButton}`]: {
    color: "#4c4c4c",
    marginBottom: "-1em",
    padding: 0,
    "& > *": {
      margin: theme.spacing(1),
    },
  },

  [`& .${classes.controlText}`]: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.mapIconToggle}`]: {
    position: "absolute",
    top: "6px",
    right: "6px",
    fontSize: "12px",
    [theme.breakpoints.down("sm")]: {
      right: "16px",
    },
  },

  [`& .${classes.metaItemContainer}`]: {
    display: "flex",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.metaItem}`]: {
    display: "flex",
    flexDirection: "column",
    margin: "0 1em",
    fontSize: "0.8rem",
    [theme.breakpoints.down("sm")]: {
      margin: "1em 0",
    },
  },

  [`& .${classes.metaIconMultiContainer}`]: {
    display: "flex",
  },

  [`& .${classes.metaIconContainer}`]: {
    display: "flex",
    alignItems: "center",
  },

  [`& .${classes.metaIconContainerSpaced}`]: {
    display: "flex",
    alignItems: "center",
    marginLeft: "2em",
    "&:first-child": {
      marginLeft: 0,
    },
  },

  [`& .${classes.metaIcon}`]: {
    fontSize: "1.8rem",
    height: "1em",
    marginRight: "0.2em",
    color: "#005287",
  },

  [`& .${classes.metaIconTitle}`]: {
    color: "#4c4c4c",
    width: "100%",
    borderBottom: "1px solid #d3d3d3",
    paddingBottom: "2px",
    fontSize: "0.7rem",
    textAlign: "left",
    textTransform: "uppercase",
    marginBottom: "0.5em",
  },

  [`& .${classes.metaIconLabel}`]: {
    textTransform: "capitalize",
  },

  [`& .${classes.hazards}`]: {
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.hazard}`]: {
    display: "flex",
    alignItems: "center",
    height: "4em",
  },

  [`& .${classes.hazardImage}`]: {
    height: "1.6em",
    margin: "auto",
  },

  [`& .${classes.hazardTitle}`]: {
    fontSize: "0.8rem",
    color: "#757575",
    textTransform: "capitalize",
  },

  [`& .${classes.mapIcon}`]: {
    margin: "0 0.4rem",
  },

  [`& .${classes.metaInfoCap}`]: {
    textTransform: "capitalize",
  }
}));

const Analysis = ({ analysis, setAnalysisGeom }) => {

  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [geographicUnit, setGeographicUnit] = useState();
  const [mapText, setMapText] = useState("Show On Map");
  const [downloadsText, setDownloadsText] = useState("Show Downloads");
  const [geomAdded, setGeomAdded] = useState(false);

  const hazardIcons = {
    earthquake: earthquakeIcon,
    flood: floodIcon,
    hurricane: hurricaneIcon,
    tsunami: tsunamiIcon,
  };

  const onClick = (e) => {
    setDownloadsText(open ? "Show Downloads" : "Hide Downloads");
    setOpen(!open);
  };

  const showOnMap = async () => {
    setMapText(!geomAdded ? "Remove from Map" : "Show on Map");
    setGeomAdded(!geomAdded);
    setAnalysisGeom(analysis.id, analysis.geom_id);
  };

  const Hazard = ({ name, image }) => {
    return (
      <div className={classes.metaIconContainerSpaced}>
        <img src={image} alt={name} className={classes.metaIcon} />
        <span className={classes.metaIconLabel}>{name}</span>
      </div>
    );
  };

  useEffect(() => {
    if (analysis.geographicUnit === "county" && analysis.geographicCount > 1) {
      setGeographicUnit("counties");
    }
    if (analysis.geographicUnit !== "county" && analysis.geographicCount > 1) {
      setGeographicUnit(`${analysis.geographicUnit}s`);
    }
  }, []);

  return (
	<Root className={classes.root}>
		<div>
		    <StyledPaper className={classes.styledPaper} elevation={3}>
		      <div className={classes.analysis}>
		        <div className={classes.summary}>
		          <span className={classes.header}>{analysis.name}</span>
		          <div className={classes.meta}>
		            <span className={classes.metaName}>Source:</span>
		            <span className={classes.metaInfo}>{analysis.source}</span>
		          </div>
		          <div className={classes.meta}>
		            <span className={classes.metaName}>Analysis Date:</span>
		            <span className={classes.metaInfo}>
		              <Moment format="MM/DD/YYYY">{analysis.date.toString()}</Moment>
		            </span>
		          </div>
		          <div className={classes.meta}>
		            <span className={classes.metaName}>Analysis Type:</span>
		            <p
		              className={
		                analysis.analysisType === "historic"
		                  ? classes.metaInfoCap
		                  : classes.metaInfo
		              }
		            >
		              {analysis.analysisType}
		            </p>
		          </div>
		          {Object.keys(analysis.meta).map((key) => {
		            return (
		              <div className={classes.meta}>
		                <span className={classes.metaName}>{key}:</span>
		                <span className={classes.metaInfo}>
		                  {analysis.meta[key].toString()}
		                </span>
		              </div>
		            );
		          })}
		        </div>
		        <div className={classes.description}>
		          <div className={classes.metaItemContainer}>
		            <div className={classes.metaItem}>
		              <span className={classes.metaIconTitle}>Hazards</span>
		              <div className={classes.metaIconMultiContainer}>
		                {analysis.hazard
		                  ? analysis.hazard.map((haz, index) => {
		                      return (
		                        <Hazard
		                          name={haz}
		                          image={hazardIcons[haz]}
		                          key={index}
		                        />
		                      );
		                    })
		                  : ""}
		              </div>
		            </div>
		            {analysis.geographicUnit && (
		              <div className={classes.metaItem}>
		                <span className={classes.metaIconTitle}>Geography</span>
		                <div className={classes.metaIconContainer}>
		                  <FontAwesomeIcon className={classes.metaIcon} icon={faCity} />
		                  <div>
		                    {analysis.geographicCount.toLocaleString()}
		                    <br />
		                    <span className={classes.metaIconLabel}>
		                      {geographicUnit}
		                    </span>
		                  </div>
		                </div>
		              </div>
		            )}
		            {analysis.losses && (
		              <div className={classes.metaItem}>
		                <span className={classes.metaIconTitle}>Losses</span>
		                <div className={classes.metaIconContainer}>
		                  <FontAwesomeIcon
		                    className={classes.metaIcon}
		                    icon={faDollarSign}
		                  />
		                  <div>
		                    {analysis.losses.toLocaleString()}
		                    <br />
		                    <span className={classes.metaIconLabel}>
		                      {analysis.lossesUnit}
		                    </span>
		                  </div>
		                </div>
		              </div>
		            )}
		            {analysis.modifiedInventory ? (
		              <div className={classes.metaItem}>
		                <span className={classes.metaIconTitle}>Inventory</span>
		                <div className={classes.metaIconContainer}>
		                  <FontAwesomeIcon className={classes.metaIcon} icon={faEdit} />
		                  <span className={classes.metaIconLabel}>Modified</span>
		                </div>
		              </div>
		            ) : (
		              <div className={classes.metaItem}>
		                <span className={classes.metaIconTitle}>Inventory</span>
		                <div className={classes.metaIconContainer}>
		                  <FontAwesomeIcon
		                    className={classes.metaIcon}
		                    icon={faDatabase}
		                  />
		                  <span className={classes.metaIconLabel}>Baseline</span>
		                </div>
		              </div>
		            )}
		          </div>
		          <div className={classes.controlButtonContainer}>
		            <Button
		              aria-label="show analysis on map"
		              className={classes.controlButton}
		              onClick={showOnMap}
		            >
		              <span className={classes.controlText}>{mapText}</span>
		              <FontAwesomeIcon
		                className={classes.mapIcon}
		                icon={faMap}
		                aria-label="show/hide analysis on map"
		              />
		              {geomAdded ? (
		                <FontAwesomeIcon
		                  className={classes.mapIconToggle}
		                  icon={faMinusCircle}
		                />
		              ) : (
		                <FontAwesomeIcon
		                  className={classes.mapIconToggle}
		                  icon={faPlusCircle}
		                />
		              )}
		            </Button>
		            <Button
		              className={classes.controlButton}
		              onClick={onClick}
		              aria-label={open ? "hide downloads" : "show downloads"}
		            >
		              <span className={classes.controlText}>{downloadsText}</span>
		              {open ? (
		                <ExpandLessIcon aria-label="hide downloads" alt="expand less" />
		              ) : (
		                <KeyboardArrowDownIcon
		                  aria-label="show downloads"
		                  alt="expand more"
		                />
		              )}
		            </Button>
		          </div>
		        </div>
		      </div>
		      {open ? <Downloads downloads={analysis.downloads} /> : ""}
		    </StyledPaper>
		</div>
	</Root>
  );
};

Analysis.propTypes = {
  setAnalysisGeom: PropTypes.func.isRequired,
};

export default connect(null, { setAnalysisGeom })(Analysis);
